/* eslint-disable no-underscore-dangle */
import { DEFAULT_MAP_CONFIG, MAP_INTERACTIVE } from '@constants/map';
import { calculateTranslation, calculateZoomFactor } from '@helpers/map';
import safeExecute from '@utils/safeExecute';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import Two from 'two.js';
import { ZUI } from 'two.js/extras/jsm/zui';
import { Group as TwoGroup } from 'two.js/src/group';
import { Shape as TwoShape } from 'two.js/src/shape';

declare global {
  interface Window {
    zui: ZUI;
  }
}

type ShapeNListener = {
  elem: HTMLElement;
  onClick: (e: Event) => void;
  onMouseDown: (e: Event) => void;
};

export type CampingMapConfig = {
  mapSVG: SVGElement | undefined;
  mappingData: any;
  onNodeClick: (nodeId: string, e: Event) => Promise<void> | void;
};

const useCampingMap = (config: CampingMapConfig) => {
  const [containerRef, setContainerRef] =
    useState<RefObject<SVGSVGElement> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [mapRes, setMapRes] = useState<(number | undefined)[]>([
    undefined,
    undefined,
    0,
    0,
  ]);
  const mapSVG = useMemo(() => config.mapSVG, [config.mapSVG]);
  const instance = useMemo(() => {
    if (containerRef?.current) {
      return new Two({
        ...DEFAULT_MAP_CONFIG,
        domElement: containerRef.current as unknown as HTMLElement,
        width: containerRef.current?.clientWidth,
        height: containerRef.current?.clientHeight,
        ...config,
      });
    }
    return null;
  }, [config, containerRef]);
  const [nodeList, setNodeList] = useState<TwoShape[]>([]);
  const campMap = useMemo<TwoGroup>(() => {
    const group = new Two.Group();
    if (nodeList) {
      group.add(
        ...nodeList.map((node: TwoShape) => {
          return node;
        }),
      );
    }
    return group;
  }, [nodeList]);

  useEffect(() => {
    setLoading(true);
    if (!instance || !mapSVG) return;
    const group = instance.interpret(mapSVG, false, false);
    setNodeList([...group.children]);
  }, [instance, mapSVG]);

  const handleNodeClick = useCallback(
    (id: string) => (e: Event) => {
      safeExecute(() => config.onNodeClick(id, e));
    },
    [config],
  );

  const bindZUI = useCallback(() => {
    if (!instance) return () => {};
    const { domElement } = instance.renderer;
    const zui = new ZUI(campMap);
    const mouse = new Two.Vector();
    const touches: Touch[] = [];
    let distance = 0;
    const [mapW, mapH, offsetX, offsetY] = mapRes;
    const [containerW, containerH] = [
      instance.renderer.width,
      instance.renderer.height,
    ];
    if (mapW && mapH && containerW && containerH && offsetX && offsetY) {
      zui.translateSurface(-offsetX, -offsetY);
      const zoomFactor = calculateZoomFactor(
        mapW,
        mapH,
        containerW,
        containerH,
      );
      zui.addLimits(zoomFactor, 2);
      zui.zoomSet(zoomFactor, 0 - window.scrollX, 0 - window.scrollY);
      const [transX, transY] = calculateTranslation(
        mapW,
        mapH,
        containerW,
        containerH,
      );
      zui.translateSurface(transX, transY);
    }

    function calculateTranslationAvailable(dx: number, dy: number) {
      // @ts-ignore
      const mapElem: SVGGElement = campMap._renderer.elem;
      const mapBBox = mapElem.getBBox();
      const matrix = mapElem.getScreenCTM();
      const offset = containerRef?.current?.getBoundingClientRect();
      if (matrix) {
        const x =
          matrix.a * mapBBox.x +
          matrix.c * mapBBox.y +
          matrix.e -
          (offset?.left ?? 0);
        const y =
          matrix.b * mapBBox.x +
          matrix.d * mapBBox.y +
          matrix.f -
          (offset?.top ?? 0);
        const xw = x + mapBBox.width * zui.scale;
        const yh = y + mapBBox.height * zui.scale;
        let panX = dx;
        let panY = dy;

        if (dx > 0) {
          if (x + dx > 0 && xw + dx > containerW) panX = 0;
        } else if (x + dx < 0 && xw + dx < containerW) panX = 0;

        if (dy > 0) {
          if (y + dy > 0 && yh + dy > containerH) panY = 0;
        } else if (y + dy < 0 && yh + dy < containerH) panY = 0;
        return [panX, panY];
      }
      return [0, 0];
    }

    function calculateZoomPositionAvailable(clientX: number, clientY: number) {
      // @ts-ignore
      const mapElem: SVGGElement = campMap._renderer.elem;
      const mapBBox = mapElem.getBBox();
      const matrix = mapElem.getScreenCTM();
      const offset = containerRef?.current?.getBoundingClientRect();
      if (matrix) {
        const x =
          matrix.a * mapBBox.x +
          matrix.c * mapBBox.y +
          matrix.e -
          (offset?.left ?? 0);
        const y =
          matrix.b * mapBBox.x +
          matrix.d * mapBBox.y +
          matrix.f -
          (offset?.top ?? 0);
        const xw = x + mapBBox.width * zui.scale;
        const yh = y + mapBBox.height * zui.scale;

        let [resX, resY] = [clientX, clientY];
        if (clientX < x) resX = xw;
        else if (clientX > xw) resX = x;
        if (clientY < y) resY = yh;
        else if (clientY > yh) resY = y;
        return [resX, resY];
      }
      return [clientX, clientY];
    }

    function mousemove(e: MouseEvent) {
      if (!MAP_INTERACTIVE) return;
      const dx = e.clientX - mouse.x;
      const dy = e.clientY - mouse.y;
      const [panX, panY] = calculateTranslationAvailable(dx, dy);
      zui.translateSurface(panX, panY);
      mouse.set(e.clientX, e.clientY);
    }

    function mouseup() {
      if (!MAP_INTERACTIVE) return;
      window.removeEventListener('mousemove', mousemove, false);
      window.removeEventListener('mouseup', mouseup, false);
    }

    function mousedown(e: MouseEvent) {
      if (!MAP_INTERACTIVE) return;
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      window.addEventListener('mousemove', mousemove, false);
      window.addEventListener('mouseup', mouseup, false);
    }

    function mousewheel(e: WheelEvent) {
      e.preventDefault();
      if (!MAP_INTERACTIVE) return;
      const dy = -e.deltaY / 1000;
      const [zoomX, zoomY] = calculateZoomPositionAvailable(
        e.clientX,
        e.clientY,
      );
      zui.zoomBy(dy, zoomX, zoomY);
    }

    // Binding touch event
    function touchend(e: TouchEvent) {
      if (!MAP_INTERACTIVE) return;
      touches.length = 0;
      const touch = e.touches[0];
      if (touch) {
        // Pass through for panning after pinching
        mouse.x = touch.clientX;
        mouse.y = touch.clientY;
      }
    }

    function panStart(e: TouchEvent) {
      if (!MAP_INTERACTIVE) return;
      const touch = e.touches[0];
      mouse.x = touch.clientX;
      mouse.y = touch.clientY;
    }

    function panMove(e: TouchEvent) {
      e.preventDefault();
      if (!MAP_INTERACTIVE) return;
      const touch = e.touches[0];
      const dx = touch.clientX - mouse.x;
      const dy = touch.clientY - mouse.y;
      const [panX, panY] = calculateTranslationAvailable(dx, dy);
      zui.translateSurface(panX, panY);
      mouse.set(touch.clientX, touch.clientY);
    }

    function pinchStart(e: TouchEvent) {
      if (!MAP_INTERACTIVE) return;
      const a = e.touches[0];
      const b = e.touches[1];
      const dx = b.clientX - a.clientX;
      const dy = b.clientY - a.clientY;
      distance = Math.sqrt(dx * dx + dy * dy);
      mouse.x = dx / 2 + a.clientX;
      mouse.y = dy / 2 + a.clientY;
    }

    function pinchMove(e: TouchEvent) {
      e.preventDefault();
      if (!MAP_INTERACTIVE) return;
      const a = e.touches[0];
      const b = e.touches[1];
      const dx = b.clientX - a.clientX;
      const dy = b.clientY - a.clientY;
      const d = Math.sqrt(dx * dx + dy * dy);
      const delta = d - distance;
      const [panX, panY] = calculateTranslationAvailable(dx, dy);
      zui.zoomBy(delta / 250, panX, panY);
      distance = d;
    }

    function touchstart(e: TouchEvent) {
      switch (e.touches.length) {
        case 2:
          pinchStart(e);
          break;
        case 1:
          panStart(e);
          break;
        default:
          break;
      }
    }

    function touchmove(e: TouchEvent) {
      switch (e.touches.length) {
        case 2:
          pinchMove(e);
          break;
        case 1:
          panMove(e);
          break;
        default:
          break;
      }
    }

    domElement.addEventListener('mousedown', mousedown, false);
    domElement.addEventListener('mousewheel', mousewheel, false);
    domElement.addEventListener('wheel', mousewheel, false);
    domElement.addEventListener('touchstart', touchstart, false);
    domElement.addEventListener('touchmove', touchmove, false);
    domElement.addEventListener('touchend', touchend, false);
    domElement.addEventListener('touchcancel', touchend, false);

    return () => {
      domElement.removeEventListener('mousedown', mousedown);
      domElement.removeEventListener('mousewheel', mousewheel);
      domElement.removeEventListener('wheel', mousewheel);
      domElement.removeEventListener('touchstart', touchstart);
      domElement.removeEventListener('touchmove', touchmove);
      domElement.removeEventListener('touchend', touchend);
      domElement.removeEventListener('touchcancel', touchend);
    };
  }, [instance, campMap, mapRes, containerRef]);

  // Bind ZUI for user interactive
  useEffect(() => {
    return bindZUI();
  }, [bindZUI]);

  // Update map
  useEffect(() => {
    if (instance) {
      instance.clear();
      instance.add(campMap);
      instance.update();

      // @ts-ignore
      const bBox = campMap?._renderer?.elem?.getBBox();
      if (bBox) setMapRes([bBox.width, bBox.height, bBox.x, bBox.y]);
      const listeners: ShapeNListener[] = campMap.children.map(
        (ele: TwoShape) => {
          const { elem }: { elem: HTMLElement } = ele.renderer;
          let move = false;
          const onClick = (e: Event) => {
            if (!move) handleNodeClick(ele.id)(e);
          };
          const onMouseDown = () => {
            move = false;
            const onMouseMove = () => {
              move = true;
            };
            const onMouseUp = () => {
              elem.removeEventListener('mousemove', onMouseMove);
              elem.removeEventListener('mouseup', onMouseUp);
            };
            elem.addEventListener('mousemove', onMouseMove, false);
            elem.addEventListener('mouseup', onMouseUp, false);
          };
          safeExecute(async () => {
            if (Object.keys(config.mappingData).includes(elem.id))
              elem.classList.add('glc-node');
            elem.addEventListener('mousedown', onMouseDown, false);
            elem.addEventListener('touchstart', onMouseDown, false);
            elem.addEventListener('click', onClick, false);
            elem.addEventListener('touch', onClick, false);
          });
          return {
            elem,
            onClick,
            onMouseDown,
          };
        },
      );

      setLoading(false);

      return () => {
        // Prevent mem leaks
        listeners.forEach(({ elem, onClick, onMouseDown }) => {
          safeExecute(async () => {
            elem.removeEventListener('mousedown', onMouseDown);
            elem.removeEventListener('touchstart', onMouseDown);
            elem.removeEventListener('click', onClick);
            elem.removeEventListener('touch', onClick);
          });
        });
      };
    }
    return () => {};
  }, [handleNodeClick, instance, campMap, config.mappingData]);

  // First render
  useEffect(() => {
    if (instance) {
      instance.render();
    }
  }, [instance]);

  return {
    instance,
    loading,
    setContainerRef,
  };
};

export default useCampingMap;
